import * as THREE from "three";
import { PET_MOVE } from "./petGait";

/**
 * The pet's looping clips: standing, trotting, galloping, napping, the zoomies (a wild pup's), the boss's charge, and
 * the birds' flight (2026-10-23: fly, hover, glide).
 */
export type PetLoop = "idle" | "walk" | "run" | "sleep" | "zoomies" | "boss_run" | "fly" | "hover" | "glide" | "hurry";
const LOOPS: readonly PetLoop[] = ["idle", "walk", "run", "sleep", "zoomies", "boss_run", "fly", "hover", "glide", "hurry"];
const LOCOMOTION = new Set<PetLoop>(["walk", "run", "boss_run", "hurry"]);
/** A flight loop a model lacks falls back to this one (a bird without the flight clips hops through the air as before). */
const FLIGHT_FALLBACK: Readonly<Partial<Record<PetLoop, PetLoop>>> = { fly: "run", hover: "run", glide: "fly", hurry: "run" };
/** The parts the layered strike leaves to the loop under it: the legs (a bird's wings, `arm_*`, take part in its strike). */
const LEGS = /(?:^|[._])(?:arm|boot)_[lr]\./;
const BIRD_LEGS = /(?:^|[._])boot_[lr]\./;
/** The layered strike fades in and out over this long (s): a blink compared with the loops' crossfade. */
const STRIKE_FADE = 0.06;

/**
 * The weights of the pet's clips, eased (pure, so test/petGait.test.ts pins it): one loop wanted at a time, the others
 * fading out as it fades in over `fade` seconds; a one-shot (an act) fading in over the loops and out again before
 * its end. What each clip finally weighs is its loop weight under the act's share.
 */
export class PetBlend {
  readonly loop: Record<PetLoop, number> = { idle: 1, walk: 0, run: 0, sleep: 0, zoomies: 0, boss_run: 0, fly: 0, hover: 0, glide: 0, hurry: 0 };
  wanted: PetLoop = "idle";
  /** The act's share (0 … 1) and whether it is still wanted (else it fades out). */
  act = 0;
  actOn = false;

  constructor(private readonly fade: number = PET_MOVE.fade) {}

  step(dt: number): void {
    const k = dt / this.fade;
    for (const n of LOOPS) this.loop[n] = n === this.wanted ? Math.min(1, this.loop[n] + k) : Math.max(0, this.loop[n] - k);
    this.act = this.actOn ? Math.min(1, this.act + k) : Math.max(0, this.act - k);
  }

  /** The weight clip `n` (a loop) draws with now. */
  weightOf(n: PetLoop): number {
    const sum = LOOPS.reduce((s, l) => s + this.loop[l], 0);
    return sum > 0 ? (this.loop[n] / sum) * (1 - this.act) : 0;
  }
}

/** Where a gait joining the blend starts: in step with the gait it takes over from (its cycle's share), else at 0. */
export function joinPhase(blend: PetBlend, phaseOf: (n: PetLoop) => number, joining: PetLoop): number {
  if (!LOCOMOTION.has(joining)) return 0;
  let best: PetLoop | null = null;
  for (const n of LOCOMOTION) if (n !== joining && blend.loop[n] > 0 && (best === null || blend.loop[n] > blend.loop[best])) best = n;
  return best === null ? 0 : phaseOf(best);
}

/**
 * The pet's clips on its HQ model (one mixer): the loops cross-fade into each other (PET_MOVE.fade), a gait joins in
 * step with the one it replaces, the locomotion clips play at the rate the move asks (petHop.ts `rate`), and a
 * one-shot (bark, cheer, hide, fidget) fades in over the loop and out again. A model without a clip just skips it.
 */
export class PetAnim {
  private readonly mixer: THREE.AnimationMixer | null;
  private readonly loops = new Map<PetLoop, THREE.AnimationAction>();
  private readonly acts = new Map<string, THREE.AnimationAction>();
  readonly blend = new PetBlend();
  private act: THREE.AnimationAction | null = null;
  private actName = "";
  private rate = 1;
  /** Each loop's weight before this frame's step (reused). */
  private readonly before = new Map<PetLoop, number>();
  /**
   * The strikes layered over the loops (petFight.ts): each one-shot's motion relative to its first frame (the rest
   * pose), the legs left out, played additively — the body lunges, the head snaps and the jaw shuts while the legs
   * keep the stride or the stance under it, so a run flows into its bite instead of cutting to a standing one.
   */
  private readonly strikes = new Map<string, THREE.AnimationAction>();
  private strike: THREE.AnimationAction | null = null;

  constructor(root: THREE.Object3D, clips: readonly THREE.AnimationClip[], bird = false) {
    this.mixer = clips.length > 0 ? new THREE.AnimationMixer(root) : null;
    if (!this.mixer) return;
    for (const c of clips) {
      if (c.name === "bite") this.strikes.set(c.name, this.layered(c, bird ? BIRD_LEGS : LEGS));
      const a = this.mixer.clipAction(c);
      if ((LOOPS as readonly string[]).includes(c.name)) { a.setLoop(THREE.LoopRepeat, Infinity); this.loops.set(c.name as PetLoop, a); }
      else { a.setLoop(THREE.LoopOnce, 1); a.clampWhenFinished = true; this.acts.set(c.name, a); }
      a.setEffectiveWeight(0);
    }
    const idle = this.loops.get("idle");
    if (idle) { idle.time = Math.random() * idle.getClip().duration; idle.setEffectiveWeight(1).play(); }
  }

  has(name: string): boolean { return this.loops.has(name as PetLoop) || this.acts.has(name); }

  /** `clip` (a one-shot) as a strike layer: additive against its first frame, without the tracks `legs` matches. */
  private layered(clip: THREE.AnimationClip, legs: RegExp): THREE.AnimationAction {
    const c = THREE.AnimationUtils.makeClipAdditive(clip.clone(), 0, undefined, 30);
    c.name = `${clip.name}+`;
    c.tracks = c.tracks.filter((t) => !legs.test(t.name));
    const a = this.mixer!.clipAction(c, undefined, THREE.AdditiveAnimationBlendMode);
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = false;
    a.setEffectiveWeight(0);
    return a;
  }

  /**
   * Plays one-shot `name` as a strike layered over whatever the loops and acts are doing (its motion added, its legs
   * left to the stride), `startAt` seconds in. Its length, 0 when the model lacks it (the caller falls back to an act).
   */
  playStrike(name: string, startAt = 0): number {
    const a = this.strikes.get(name);
    if (!a) return 0;
    a.reset().play();
    a.time = Math.max(0, Math.min(startAt, a.getClip().duration));
    this.strike = a;
    return a.getClip().duration;
  }

  /** The loop it should be in, and (for a gait) the rate it plays at. A missing gait falls back to the walk, then idle. */
  setLoop(name: PetLoop, rate = 1): void {
    let want: PetLoop = name;
    for (let i = 0; i < 2 && !this.loops.has(want) && FLIGHT_FALLBACK[want]; i++) want = FLIGHT_FALLBACK[want]!;
    const n = this.loops.has(want) ? want : want === "run" && this.loops.has("walk") ? "walk" : "idle";
    this.blend.wanted = n;
    this.rate = rate;
  }

  /**
   * Starts one-shot `name` (fading in over the loop) `startAt` seconds in; `hold` keeps its last frame until the next act
   * or `endAct` (the caught pup sitting dizzy). Its length, or 0 when the model lacks it.
   */
  playAct(name: string, { startAt = 0, hold = false }: { startAt?: number; hold?: boolean } = {}): number {
    const a = this.acts.get(name);
    if (!a) return 0;
    if (this.act && this.act !== a) this.act.setEffectiveWeight(0).stop();
    a.reset().play();
    if (startAt > 0) a.time = Math.min(startAt, a.getClip().duration);
    this.act = a; this.actName = name; this.holding = hold;
    this.blend.actOn = true;
    return a.getClip().duration;
  }
  private holding = false;

  /** The act playing now ("" when none or fading out). */
  get acting(): string { return this.act && this.blend.actOn ? this.actName : ""; }

  /** Fades the act out now (a move cutting it, or its end coming). */
  endAct(): void { this.blend.actOn = false; this.holding = false; }

  update(dt: number): void {
    if (!this.mixer) return;
    const a = this.act;
    // the act fades out over its last stretch, so it ends on the loop rather than snapping to it
    if (a && this.blend.actOn && !this.holding && a.time >= a.getClip().duration - PET_MOVE.fade) this.blend.actOn = false;
    const before = this.before;
    for (const n of this.loops.keys()) before.set(n, this.blend.loop[n]);
    this.blend.step(dt);
    for (const [n, action] of this.loops) {
      const w = this.blend.weightOf(n);
      if (before.get(n) === 0 && this.blend.loop[n] > 0) {
        // joining the blend: a gait picks up the stride of the one it replaces (no stumble at the switch)
        action.reset().play();
        action.time = joinPhase(this.blend, (m) => this.phaseOf(m), n) * action.getClip().duration;
      }
      if (this.blend.loop[n] === 0 && action.isRunning()) action.stop();
      action.timeScale = LOCOMOTION.has(n) ? this.rate : 1;
      action.setEffectiveWeight(w);
    }
    if (a) { a.setEffectiveWeight(this.blend.act); if (this.blend.act === 0 && !this.blend.actOn) { a.stop(); this.act = null; this.actName = ""; } }
    const s = this.strike;
    if (s) {
      const d = s.getClip().duration, t = s.time;
      // a quick fade in and out at its ends (its first and last frames are the rest pose: nothing to pop either way)
      s.setEffectiveWeight(Math.max(0, Math.min(1, t / STRIKE_FADE, (d - t) / STRIKE_FADE)));
      if (!s.isRunning() || t >= d) { s.setEffectiveWeight(0).stop(); this.strike = null; }
    }
    this.mixer.update(dt);
  }

  /** Where loop `n` is in its cycle (0 … 1). */
  private phaseOf(n: PetLoop): number {
    const a = this.loops.get(n);
    if (!a) return 0;
    const d = a.getClip().duration;
    return d > 0 ? (a.time % d) / d : 0;
  }

  /** Stops everything (the body goes). */
  dispose(): void { this.mixer?.stopAllAction(); }
}
